/**
 * Contagem Excel Service
 *
 * Handles all Excel-related operations:
 * - Epic 19: upload/download Excel for a job
 * - Epic 53: import ordem de contagem / import resultado
 */
import apiClient from '../apiClient';
import type {
  UploadExcelResponse,
  ContagemDadosImportados,
  ContagemResultadoInput,
  ContagemResultadoResponse,
  ImportOrdemResponse,
  ConfirmReplaceOrdemResponse,
  ImportResultadoResponse,
} from './contagemTypes';

class ContagemExcelService {
  /**
   * Import Excel file with expected inventory
   * POST /api/jobs/{job_id}/excel/upload
   */
  async uploadExcelEpic19(
    jobId: number,
    file: File,
    clienteId?: number,
    comRegime?: boolean,
    forceReplace?: boolean
  ): Promise<UploadExcelResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (clienteId) {
      formData.append('cliente_id', clienteId.toString());
    }
    if (comRegime !== undefined) {
      formData.append('com_regime', comRegime.toString());
    }
    if (forceReplace) {
      formData.append('force_replace', 'true');
    }

    const response = await apiClient.post(`/jobs/${jobId}/excel/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Download Excel file (Epic 19)
   * GET /api/jobs/{job_id}/excel/download
   * Returns: Excel file as Blob
   */
  async downloadExcelEpic19(jobId: number): Promise<Blob> {
    const response = await apiClient.get(`/jobs/${jobId}/excel/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Helper: Download Excel and trigger browser download
   */
  async downloadAndSaveExcel(jobId: number, filename?: string): Promise<void> {
    try {
      const blob = await this.downloadExcelEpic19(jobId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `contagem_${jobId}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao fazer download do Excel');
    }
  }

  /**
   * Epic 19 - Get imported Excel items as JSON
   * GET /api/jobs/{job_id}/excel/items
   * Returns: ContagemDadosImportados or null if not found
   */
  async getExcelItemsEpic19(jobId: number): Promise<ContagemDadosImportados | null> {
    try {
      const response = await apiClient.get(`/jobs/${jobId}/excel/items`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw new Error(error.response?.data?.detail || 'Erro ao buscar dados importados');
    }
  }

  /**
   * Submit counting results from offline system
   * POST /api/contagens/{job_id}/resultados
   */
  async enviarResultados(
    jobId: number,
    resultados: ContagemResultadoInput[]
  ): Promise<ContagemResultadoResponse> {
    const response = await apiClient.post(`/contagens/${jobId}/resultados`, resultados);
    return response.data;
  }

  // ============================================================
  // EPIC 53 - IMPORTAR ORDEM/RESULTADO CONTAGEM
  // ============================================================

  /**
   * Import Ordem de Contagem Excel file (8 columns)
   * POST /api/contagens/{job_id}/importar-ordem
   * Creates new ItemContagemImportado records
   */
  async importarOrdemContagem(
    jobId: number,
    file: File,
    forceReplace: boolean = false
  ): Promise<ImportOrdemResponse | ConfirmReplaceOrdemResponse> {
    const formData = new FormData();
    formData.append('arquivo', file);
    formData.append('force_replace', String(forceReplace));

    const response = await apiClient.post(`/contagens/${jobId}/importar-ordem`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Import Resultado de Contagem Excel file (6 columns)
   * POST /api/contagens/{job_id}/importar-resultado
   * Updates quantidade_contada on existing records
   */
  async importarResultadoContagem(jobId: number, file: File): Promise<ImportResultadoResponse> {
    const formData = new FormData();
    formData.append('arquivo', file);

    const response = await apiClient.post(`/contagens/${jobId}/importar-resultado`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }
}

export default new ContagemExcelService();
